// Notification preferences: categories and quiet hours — audit finding 12.1.
import { NOTIFICATION_FILTER_GROUPS } from './notification-display.js';
export const DEFAULT_NOTIFICATION_USER_SETTINGS = {
    muted_categories: [],
    muted_types: [],
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00',
    timezone: 'UTC',
};
/** Category labels a user may mute. Mentions is excluded deliberately: it is
 *  the one notification that is always about the user personally, and silently
 *  dropping a direct mention is worse than an extra buzz. */
export const MUTABLE_NOTIFICATION_CATEGORIES = NOTIFICATION_FILTER_GROUPS.filter((group) => group.label !== 'Mentions').map((group) => group.label);
export function isMutableNotificationCategory(label) {
    return MUTABLE_NOTIFICATION_CATEGORIES.includes(label);
}
/** The category a notification type belongs to, or null if it is in none. */
export function categoryForNotificationType(type) {
    for (const group of NOTIFICATION_FILTER_GROUPS) {
        if (group.types.includes(type))
            return group.label;
    }
    return null;
}
export function isCategoryMuted(settings, type) {
    const category = categoryForNotificationType(type);
    if (!category)
        return false;
    return settings.muted_categories.includes(category);
}
// ── Per-type opt-out ────────────────────────────────────────────────────────
/** Types a user may never switch off. A mention is the one notification aimed
 *  at the user by name; silently dropping it is worse than an extra buzz. Same
 *  reasoning that excludes the Mentions category above, and mirrored by the
 *  suppression trigger in 20260811000001_notification_muted_types.sql. */
export const ALWAYS_ON_NOTIFICATION_TYPES = ['mention', 'project_message_mention'];
/** Declared in NOTIFICATION_TYPES and accepted by the DB CHECK, but nothing in
 *  the codebase ever produces them. They stay valid for stored history, and are
 *  kept out of the settings UI — a toggle for an event that cannot fire is
 *  noise. Delete an entry here the day something starts emitting it. */
export const NEVER_GENERATED_NOTIFICATION_TYPES = [
    'task_overdue',
    'task_reassigned',
    'document_updated',
];
/** The settings-screen catalog: the same grouping the filter chips use, minus
 *  types nothing emits. Derived rather than re-declared so a new notification
 *  type added to NOTIFICATION_FILTER_GROUPS gets a toggle automatically. */
export const NOTIFICATION_TYPE_GROUPS = NOTIFICATION_FILTER_GROUPS.map((group) => ({
    label: group.label,
    types: group.types.filter((type) => !NEVER_GENERATED_NOTIFICATION_TYPES.includes(type)),
    alwaysOn: group.types.every((type) => ALWAYS_ON_NOTIFICATION_TYPES.includes(type)),
})).filter((group) => group.types.length > 0);
/** Every type a user is allowed to switch off. The API filters incoming
 *  `muted_types` against this, so an unknown string or `mention` cannot be
 *  persisted. */
export const MUTABLE_NOTIFICATION_TYPES = NOTIFICATION_TYPE_GROUPS.filter((group) => !group.alwaysOn).flatMap((group) => group.types);
export function isMutableNotificationType(type) {
    return MUTABLE_NOTIFICATION_TYPES.includes(type);
}
/** Whether the user has switched this notification type off entirely.
 *
 *  Unlike `isCategoryMuted`, a true here means the notification is never
 *  created — the DB trigger drops the insert. */
export function isTypeMuted(settings, type) {
    if (ALWAYS_ON_NOTIFICATION_TYPES.includes(type))
        return false;
    return settings.muted_types.includes(type);
}
/** One line of plain English per type, shown under its label in Settings.
 *  Written from the recipient's point of view: what has to happen for this to
 *  reach you. */
export const NOTIFICATION_TYPE_DESCRIPTIONS = {
    mention: 'Someone tags your @username in a note or message.',
    project_message_mention: 'Someone tags you — or @all — in a project chat.',
    // Tasks
    task_assigned: 'A task is assigned to you.',
    task_unassigned: 'You are removed from a task you were assigned to.',
    task_created: 'A task is created in a project you administer.',
    task_updated: 'Details change on a task you are assigned to or administer.',
    task_deleted: 'A task is deleted from a project you administer.',
    task_completed: 'A task you are on is marked complete.',
    task_reopened: 'A completed task you are on is reopened.',
    task_status_changed: 'A task you are on moves to a different status.',
    task_ready_to_start: 'Everything a not-started or overdue task you are on was waiting on is done.',
    task_at_risk: 'A task you are on becomes blocked or runs past its deadline.',
    // Documents
    document_shared: 'A document is shared on a task you are on.',
    document_uploaded: 'A document is uploaded to a project you administer.',
    document_version_uploaded: 'A new version replaces a document you can see.',
    // Notes
    note_created: 'A note is added to a task you are on.',
    comment_reply: 'Someone replies to your note or to a thread you are in.',
    project_message_posted: 'Anyone posts in the chat of a project you are a member of.',
    // Projects
    project_member_added: 'You or someone else joins a project you are in.',
    project_member_removed: 'You are removed from a project.',
    project_created: 'A new project is created.',
    project_updated: 'A project you are in is edited.',
    project_archived: 'A project you are in is archived.',
    project_deleted: 'A project you are in is deleted.',
    // Team
    role_changed: 'Your role changes, or an admin changes someone else’s.',
    user_invited: 'A new user is invited to the workspace.',
    user_deactivated: 'A user account is deactivated.',
    user_reactivated: 'A user account is reactivated.',
    // Alerts
    escalation_alert: 'A task you are on is within two hours of its deadline.',
    deadline_reminder: 'A task you are on is due within a day.',
};
/** Minutes since local midnight for an 'HH:MM' string, or null if unparseable. */
export function parseTimeOfDay(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
    if (!match)
        return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59)
        return null;
    return hours * 60 + minutes;
}
/** Whether `minutesOfDay` falls inside the [start, end) window.
 *
 *  Quiet hours normally wrap midnight (22:00 → 07:00), so a naive
 *  `start <= t && t < end` comparison is inverted for the common case. */
export function isWithinQuietWindow(minutesOfDay, startMinutes, endMinutes) {
    // A zero-length window silences nothing rather than everything.
    if (startMinutes === endMinutes)
        return false;
    if (startMinutes < endMinutes) {
        return minutesOfDay >= startMinutes && minutesOfDay < endMinutes;
    }
    return minutesOfDay >= startMinutes || minutesOfDay < endMinutes;
}
/** Local minutes-since-midnight for an instant in an IANA timezone. Falls back
 *  to UTC if the zone is not recognised, rather than throwing mid-delivery. */
export function localMinutesOfDay(at, timezone) {
    try {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: timezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).formatToParts(at);
        const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
        const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
        // Some zones format midnight as "24".
        return (hour % 24) * 60 + minute;
    }
    catch {
        return at.getUTCHours() * 60 + at.getUTCMinutes();
    }
}
export function isQuietHours(settings, at) {
    if (!settings.quiet_hours_enabled)
        return false;
    const start = parseTimeOfDay(settings.quiet_hours_start);
    const end = parseTimeOfDay(settings.quiet_hours_end);
    if (start === null || end === null)
        return false;
    return isWithinQuietWindow(localMinutesOfDay(at, settings.timezone), start, end);
}
/** Whether a push may be sent for `type` right now.
 *
 *  Only PUSH is suppressed. The in-app notification is always recorded, so a
 *  muted category or a quiet hour never loses information — the user still
 *  sees everything when they next open the list.
 *
 *  Deliberately does NOT consult `muted_types`: a muted type never reaches this
 *  point, because the DB trigger drops the row before any channel dispatch. */
export function shouldSendPush(settings, type, at) {
    if (isCategoryMuted(settings, type))
        return false;
    return !isQuietHours(settings, at);
}
//# sourceMappingURL=notification-settings.js.map